import { useEffect, useRef, useState } from 'react';
// @ts-ignore
import { generateUUID } from 'three/src/math/MathUtils';
// @ts-ignore
import occtimportjs from 'occt-import-js';

export interface PartNode {
  id: string;
  meshes: number[];
  name: string;
  children: PartNode[];
}

export interface Mesh extends ImportedMesh {
  part?: string;
}

export interface StepModel {
  meshes: Mesh[];
  root: PartNode;
  parts: Record<string, number[]>;
}

export interface ImportedPartNode {
  meshes: number[];
  children: ImportedPartNode[];
  name: string;
}

export interface ImportedMesh {
  attributes: {
    position: {
      array: number[];
    };
    normal: {
      array: number[];
    };
  };
  brep_faces: {
    first: number;
    last: number;
    color: string | null;
  };
  color: [number, number, number];
  index: { array: number[] };
  name: string;
}

export interface ImportedStepFile {
  meshes: ImportedMesh[];
  root: ImportedPartNode;
  success: boolean;
}

export const useStepModel = (url: string) => {
  const [isLoading, setIsLoading] = useState(true);
  const [model, setModel] = useState<StepModel | null>(null);
  const occt = useRef(null);

  const load = async () => {
    setIsLoading(true);
    // Initialise occt
    if (!occt.current) {
      try {
        // @ts-ignore
        const occtImport = await occtimportjs({
          locateFile: () => `/occt-import-js.wasm`,
        });
        occt.current = occtImport;
      } catch (err) {
        console.error(err);
      }
    }

    // Fetch file
    const response = await fetch(url);
    const buffer = await response.arrayBuffer();

    // @ts-expect-error
    const step: ImportedStepFile = occt.current.ReadStepFile(
      new Uint8Array(buffer),
      null
    ) as ImportedStepFile;

    // TODO: tidy
    const assignIds = (node: ImportedPartNode): PartNode => {
      return {
        name: node.name,
        meshes: node.meshes,
        id: generateUUID(),
        children: node.children.map(assignIds),
      };
    };

    const getPartMapEntries = (node: PartNode): [string, number[]][] => {
      const children = node.children.flatMap(getPartMapEntries);
      return [[node.id, node.meshes], ...children];
    };

    const root = assignIds(step.root);

    const partsList = getPartMapEntries(root);
    const parts = Object.fromEntries(partsList);

    const meshes: Mesh[] = step.meshes.map((mesh, idx) => {
      // eslint-disable-next-line no-restricted-syntax
      for (const part of partsList) {
        if (part[1].includes(idx)) {
          return { ...mesh, part: part[0] };
        }
      }
      return mesh;
    });

    setModel({ root, parts, meshes });
    setIsLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  return { isLoading, model };
};

type FileType = 'step' | 'stl';

export const getFileTypeFromName = (filename: string): FileType | undefined => {
  filename = filename.toLowerCase();
  if (filename.endsWith('.step') || filename.endsWith('.stp')) return 'step';
  if (filename.endsWith('.stl')) return 'stl';
  return undefined;
};

export const getFileType = (url: string): FileType | undefined => {
  // Retrieve pathname, then extension
  const path = URL.parse(url)?.pathname;
  if (!path) return undefined;

  return getFileTypeFromName(path);
};

export const isThreads3dFile = (url: string): boolean => !!getFileType(url);
